/**
 * The turn flow on screen (docs/ux.md §2, §3, §7): the prompt, the side marks and the overlays
 * over the board (curtain, wind, confirmations, summary, result); the HUD (Hud.tsx) uses them.
 * Everything reads the turn machine; nothing here talks to the rules engine except through its
 * queries.
 */
import { DIRECTIONS, type GameState, type Side } from '@brig/rules-engine';
import { getPlayerColour } from '@brig/nations';
import { palette } from '@brig/theme/tokens';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useGameExit } from './exit.ts';
import { NationFlag } from '../nations/NationFlag.tsx';
import * as styles from './Game.css.ts';
import { Icon } from './icons.tsx';
import { current } from './history.ts';
import { shipsToMove, turnSummary } from './turn.ts';
import { WatchButton } from './ViewControls.tsx';
import type { TurnSend, TurnSnapshot } from './useTurn.ts';

const game = (snapshot: TurnSnapshot): GameState => current(snapshot.context.history);
const sideLabel = (state: GameState, side: Side) => state.players[side].name;

interface Props {
  readonly snapshot: TurnSnapshot;
  readonly send: TurnSend;
}

/**
 * A side as the board shows it: its nation's flag and its player colour next to the text, so
 * the header reads the same as the ships on the map.
 */
export function SideMark({
  snapshot,
  side,
  children,
}: {
  snapshot: TurnSnapshot;
  side: Side;
  children: ReactNode;
}) {
  const { nations, colours } = snapshot.context.history;
  return (
    <span
      className={styles.sideMark}
      style={{ ['--side-colour' as string]: getPlayerColour(colours[side]).hex }}
    >
      <NationFlag nation={nations[side]} height={14} decorative />
      {children}
    </span>
  );
}

export function Dice() {
  return (
    <span className={styles.noReturn} aria-hidden="true">
      🎲
    </span>
  );
}

/** Overlays over the board: curtain, wind, cast confirmation, end-of-turn summary, result. */
export function TurnOverlays({
  snapshot,
  send,
  nameOf,
}: Props & { readonly nameOf: (id: string) => string }) {
  const state = game(snapshot);
  if (snapshot.matches('handover')) return <Handover snapshot={snapshot} send={send} />;
  if (snapshot.matches('wind')) return <WindReveal state={state} send={send} />;
  if (snapshot.matches({ muster: 'confirming' })) return <ConfirmCast send={send} />;
  if (snapshot.matches({ battle: 'confirmingEnd' })) {
    return <EndSummary state={state} send={send} nameOf={nameOf} />;
  }
  if (snapshot.matches('over')) return <GameOver snapshot={snapshot} send={send} />;
  return null;
}

function Dialog({
  title,
  heading,
  children,
  onCancel,
  className = styles.overlay,
}: {
  title: string;
  /** What the title shows, when richer than its text (a flag, say). */
  heading?: ReactNode;
  children: ReactNode;
  onCancel?: () => void;
  className?: string;
}) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Focus the primary action (the last button), and give focus back when closed.
    const before = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    return () => before?.focus();
  }, []);
  return (
    <div className={className}>
      <div
        ref={ref}
        className={styles.card}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && onCancel) {
            e.stopPropagation();
            onCancel();
          }
        }}
      >
        <h2 id={id} className={styles.cardTitle} aria-label={heading ? title : undefined}>
          {heading ?? title}
        </h2>
        {children}
      </div>
    </div>
  );
}

/** U-TURN-01..02: the curtain before each turn; the first one tells who won the lot. */
function Handover({ snapshot, send }: Props) {
  const { t } = useTranslation();
  const state = game(snapshot);
  const side = sideLabel(state, state.turn.side);
  const first = snapshot.context.history.actions.length === 0;
  return (
    <Dialog
      title={t('game.handover.title', { side })}
      className={styles.curtain}
      heading={
        <SideMark snapshot={snapshot} side={state.turn.side}>
          {t('game.handover.title', { side })}
        </SideMark>
      }
    >
      {first ? (
        <p className={styles.prompt}>
          {state.ruleset.firstSide === 'lot'
            ? t('game.handover.firstByLot', { side })
            : t('game.handover.first', { side })}
        </p>
      ) : null}
      <p className={styles.prompt}>{t('game.prompt.handover', { side })}</p>
      <div className={styles.cardRow}>
        <WatchButton snapshot={snapshot} send={send} />
        <button
          type="button"
          className={styles.primary}
          data-autofocus
          onClick={() => {
            send({ type: 'START' });
          }}
        >
          {t('game.action.startTurn')}
        </button>
      </div>
    </Dialog>
  );
}

/**
 * U-WIND-01..03, spec v2 §14: the engine's spin result shown on a sea compass over the darkened
 * map. The needle turns a few times and lands on the result; a tap or a key skips. The UI draws
 * nothing at random: it only shows `state.turn.spin`.
 */
function WindReveal({ state, send }: { state: GameState; send: TurnSend }) {
  const { t } = useTranslation();
  const spin = state.turn.spin;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        send({ type: 'SKIP_ANIM' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [send]);
  if (!spin) return null;
  return (
    <div
      className={styles.windOverlay}
      data-testid="wind-overlay"
      onClick={() => {
        send({ type: 'SKIP_ANIM' });
      }}
    >
      <div className={styles.windCard} role="status">
        <h2 className={styles.windTitle}>{t('game.wind.title')}</h2>
        <WindDial direction={spin.direction} />
        <span className={styles.windResult}>
          <Icon name="wind" size={20} />
          {t(`game.weather.${spin.weather}`)} · {t(`game.dir.${spin.direction}`)}
        </span>
        <p className={styles.windCaption}>
          {t(`game.wind.${spin.weather}`, { dir: t(`game.dir.${spin.direction}`) })}
          {state.turn.group.length > 0
            ? ` ${t('game.wind.movers', { n: shipsToMove(state).length, total: state.turn.group.length })}`
            : ''}
        </p>
        <button type="button" className={styles.windContinue} data-autofocus>
          {t('game.action.skipAnimation')}
        </button>
      </div>
    </div>
  );
}

const angleOf = (direction: (typeof DIRECTIONS)[number]) => DIRECTIONS.indexOf(direction) * 45;

const BRONZE = '#B8893F';
const BRONZE_DEEP = '#6E5222';
const FACE = '#F0E3C4';
const NEEDLE_N = '#AD3030';
const NEEDLE_S = '#174A88';

/** An old sea compass: bronze ring, a rose on parchment, the needle pointing downwind. */
function WindDial({ direction }: { direction: (typeof DIRECTIONS)[number] }) {
  const ticks = Array.from({ length: 32 }, (_, i) => i * 11.25);
  const point = (a: number, r: number) => {
    const rad = ((a - 90) * Math.PI) / 180;
    return `${(r * Math.cos(rad)).toFixed(2)},${(r * Math.sin(rad)).toFixed(2)}`;
  };
  const star = (a: number, long: number, wide: number) =>
    `${point(a, long)} ${point(a + 45, wide)} 0,0 ${point(a - 45, wide)}`;
  return (
    <svg viewBox="-62 -62 124 124" className={styles.windDial} aria-hidden="true">
      <circle r={60} fill={BRONZE_DEEP} />
      <circle r={57} fill={BRONZE} stroke={BRONZE_DEEP} strokeWidth={1} />
      <circle r={50} fill={FACE} stroke={BRONZE_DEEP} strokeWidth={1.2} />
      {ticks.map((a) => (
        <line
          key={a}
          x1={point(a, 50).split(',')[0]}
          y1={point(a, 50).split(',')[1]}
          x2={point(a, a % 45 === 0 ? 43 : 46.5).split(',')[0]}
          y2={point(a, a % 45 === 0 ? 43 : 46.5).split(',')[1]}
          stroke={palette.ink}
          strokeWidth={a % 45 === 0 ? 1 : 0.5}
        />
      ))}
      {[45, 135, 225, 315].map((a) => (
        <polygon
          key={a}
          points={star(a, 26, 6)}
          fill={palette.paperShade}
          stroke={palette.ink}
          strokeWidth={0.5}
        />
      ))}
      {[0, 90, 180, 270].map((a) => (
        <polygon
          key={a}
          points={star(a, 38, 8)}
          fill={palette.paperDeep}
          stroke={palette.ink}
          strokeWidth={0.6}
        />
      ))}
      {(['N', 'E', 'S', 'W'] as const).map((d, i) => {
        const [x, y] = point(i * 90, 55).split(',');
        return (
          <text
            key={d}
            x={x}
            y={Number(y) + 3}
            fontSize={14}
            textAnchor="middle"
            fill={FACE}
            fontFamily="'PT Serif', serif"
          >
            {d}
          </text>
        );
      })}
      <g className={styles.windArrow} style={{ ['--wind-angle' as string]: `${angleOf(direction)}deg` }}>
        <path d="M0 -44 L6 0 L-6 0 Z" fill={NEEDLE_N} stroke={palette.ink} strokeWidth={0.6} />
        <path d="M0 44 L6 0 L-6 0 Z" fill={NEEDLE_S} stroke={palette.ink} strokeWidth={0.6} />
      </g>
      <circle r={5} fill={BRONZE} stroke={BRONZE_DEEP} strokeWidth={1} />
      <circle r={1.8} fill={BRONZE_DEEP} />
    </svg>
  );
}

/** Small downwind arrow for the turn header. */
export function WindArrow({ direction, size }: { direction: (typeof DIRECTIONS)[number]; size: number }) {
  return (
    <svg viewBox="-10 -10 20 20" width={size} height={size} aria-hidden="true">
      <g transform={`rotate(${angleOf(direction)})`}>
        <path d="M0 -8 L4 2 L0 0 L-4 2 Z" fill="currentColor" />
      </g>
    </svg>
  );
}

/** U-MUST-04 */
function ConfirmCast({ send }: { send: TurnSend }) {
  const { t } = useTranslation();
  const [dontAsk, setDontAsk] = useState(false);
  const cancel = () => {
    send({ type: 'CANCEL' });
  };
  return (
    <Dialog title={t('game.confirmCast.title')} onCancel={cancel}>
      <p className={styles.prompt}>{t('game.confirmCast.body')}</p>
      <label>
        <input
          type="checkbox"
          checked={dontAsk}
          onChange={(e) => {
            setDontAsk(e.target.checked);
          }}
        />{' '}
        {t('game.confirmCast.dontAsk')}
      </label>
      <div className={styles.cardRow}>
        <button type="button" className={styles.button} onClick={cancel}>
          {t('game.confirmCast.back')}
        </button>
        <button
          type="button"
          className={styles.primary}
          data-autofocus
          onClick={() => {
            send({ type: 'CONFIRM_CAST', dontAskAgain: dontAsk });
          }}
        >
          <Dice />
          {t('game.confirmCast.confirm')}
        </button>
      </div>
    </Dialog>
  );
}

/** U-END-02: what would go unused, each unit a way back to it. */
function EndSummary({
  state,
  send,
  nameOf,
}: {
  state: GameState;
  send: TurnSend;
  nameOf: (id: string) => string;
}) {
  const { t } = useTranslation();
  const summary = turnSummary(state);
  const cancel = () => {
    send({ type: 'CANCEL' });
  };
  const group = (key: 'game.summary.notMoved' | 'game.summary.notFired', ids: readonly string[]) =>
    ids.length > 0 ? (
      <div>
        <strong>{t(key, { n: ids.length })}</strong>
        <ul className={styles.list}>
          {ids.map((id) => (
            <li key={id}>
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => {
                  send({ type: 'SELECT', id });
                }}
              >
                {nameOf(id)}
              </button>
            </li>
          ))}
        </ul>
      </div>
    ) : null;
  return (
    <Dialog title={t('game.summary.title')} onCancel={cancel}>
      {group('game.summary.notMoved', summary.notMoved)}
      {group('game.summary.notFired', summary.notFired)}
      <div className={styles.cardRow}>
        <button type="button" className={styles.button} onClick={cancel}>
          {t('game.action.keepPlaying')}
        </button>
        <button
          type="button"
          className={styles.primary}
          data-autofocus
          onClick={() => {
            send({ type: 'CONFIRM_END' });
          }}
        >
          {t('game.action.endTurn')}
        </button>
      </div>
    </Dialog>
  );
}

/** Confetti pieces: where each falls from (% of the width), its delay, and a turn. */
const CONFETTI = Array.from({ length: 28 }, (_, i) => ({
  left: (i * 37) % 100,
  delay: ((i * 7) % 10) * 0.12,
  turn: (i * 53) % 360,
}));

/**
 * U-OVER-01: the end of the game, a card over the dimmed board. A win gets the winner's flag
 * under a crown and confetti in their colour; a loss to the computer and a draw stay plain.
 * The card can be put aside to look at the board, and it offers to watch the last turn; its
 * primary action leads back to the logbook.
 */
function GameOver({ snapshot, send }: Props) {
  const { t } = useTranslation();
  const exitTo = useGameExit();
  const [closed, setClosed] = useState(false);
  const state = game(snapshot);
  const status = state.status;
  if (status.kind === 'playing' || closed) return null;
  const { nations, colours } = snapshot.context.history;
  const winner = status.kind === 'won' ? status.winner : null;
  const lost = winner !== null && winner === snapshot.context.remote;
  const colour = winner ? getPlayerColour(colours[winner]).hex : palette.gold;
  const afloat = (side: Side) => state.ships.filter((s) => s.side === side && !s.sunk).length;
  const total = (side: Side) => state.ships.filter((s) => s.side === side).length;
  return (
    <div className={styles.overBackdrop}>
      {winner && !lost ? (
        <div className={styles.confetti} aria-hidden="true">
          {CONFETTI.map((c, i) => (
            <span
              key={i}
              style={{
                left: `${c.left}%`,
                animationDelay: `${c.delay}s`,
                rotate: `${c.turn}deg`,
                background: [colour, palette.gold, palette.paper][i % 3],
              }}
            />
          ))}
        </div>
      ) : null}
      <div
        className={styles.overCard}
        role="dialog"
        aria-modal="false"
        aria-labelledby="game-over-title"
        style={{ ['--winner' as string]: colour }}
      >
        <button
          type="button"
          className={styles.overClose}
          aria-label={t('game.over.close')}
          title={t('game.over.close')}
          onClick={() => {
            setClosed(true);
          }}
        >
          <Icon name="close" size={18} />
        </button>
        {winner ? (
          <div className={styles.overCrest} aria-hidden="true">
            {lost ? null : <span className={styles.overCrown}>♛</span>}
            <NationFlag nation={nations[winner]} height={44} decorative />
          </div>
        ) : null}
        <p className={styles.overKicker}>
          {winner ? (lost ? t('game.over.defeat') : t('game.over.victory')) : t('game.over.end')}
        </p>
        <h2 id="game-over-title" className={styles.overTitle}>
          {winner ? t('game.over.winner', { side: sideLabel(state, winner) }) : t('game.over.draw')}
        </h2>
        {status.kind === 'won' ? (
          <p className={styles.overReason}>{t(`game.over.${status.reason}`)}</p>
        ) : null}
        <dl className={styles.overStats}>
          <div>
            <dt>{t('game.over.turn')}</dt>
            <dd>{state.turn.number}</dd>
          </div>
          {(['red', 'blue'] as const).map((side) => (
            <div key={side}>
              <dt>
                <NationFlag nation={nations[side]} height={12} decorative />
                {t('game.over.afloat', { side: sideLabel(state, side) })}
              </dt>
              <dd>
                {afloat(side)}
                <small>/{total(side)}</small>
              </dd>
            </div>
          ))}
        </dl>
        <div className={styles.overActions}>
          <WatchButton snapshot={snapshot} send={send} />
          <Link to="/how-to-play" className={styles.button}>
            {t('learn.open')}
          </Link>
          <button
            type="button"
            className={styles.button}
            onClick={() => {
              setClosed(true);
            }}
          >
            {t('game.over.close')}
          </button>
          <Link to={exitTo} className={styles.primaryLink} data-testid="game-over-logbook">
            {t('game.over.leave')}
          </Link>
        </div>
      </div>
    </div>
  );
}
